import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import type { Money } from '@/api/types';
import { inZone, nowInZone } from './time';

dayjs.extend(relativeTime);

const rupees = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Money arrives as a decimal string. It is parsed *once*, here, only to be
 * grouped and symbol-prefixed for display — never to be arithmetic'd on. Any
 * sum the user will act on is computed server-side in integer paise.
 */
export function formatMoney(value: Money | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const amount = Number(value);
  return Number.isFinite(amount) ? rupees.format(amount) : String(value);
}

export function formatPercentage(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const amount = Number(value);
  return Number.isFinite(amount) ? `${amount.toFixed(2)}%` : String(value);
}

export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat('en-IN').format(value);
}

/** Signed, so a ledger row reads as a movement rather than a quantity. */
export function formatDelta(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  if (value === 0) return '0';
  return `${value > 0 ? '+' : ''}${formatNumber(value)}`;
}

/*
 * All four read the PLATFORM's clock, not the machine the till runs on. See
 * `time.ts` — this is the single place "whose 9am?" gets answered.
 */

export function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  return inZone(value).format('DD MMM YYYY');
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  return inZone(value).format('DD MMM YYYY, HH:mm');
}

/**
 * A distance between two instants has no timezone, so this one deliberately
 * does not convert — doing so would change nothing and imply that it had.
 */
export function fromNow(value: string | null | undefined): string {
  if (!value) return '—';
  return dayjs(value).fromNow();
}

/**
 * Negative once the date is past — the caller decides how loudly to say so.
 *
 * Both ends snap to midnight on the platform clock. On the till's own clock a
 * batch expiring tomorrow could report as expiring today, and for an expiry
 * warning that is the difference between sellable and not.
 */
export function daysUntil(value: string | null | undefined): number | null {
  if (!value) return null;
  return inZone(value).startOf('day').diff(nowInZone().startOf('day'), 'day');
}

export function fullName(user: {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
}): string {
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return name || user.email || user.phone || 'Unnamed user';
}

export function initials(user: { firstName?: string | null; lastName?: string | null }): string {
  const letters = [user.firstName?.[0], user.lastName?.[0]].filter(Boolean).join('');
  return (letters || '?').toUpperCase();
}

/**
 * `SUPER_ADMIN` → `Super admin`. The wire name, made readable.
 *
 * The account row was doing `role.replace('_', ' ').toLowerCase()` inline,
 * which replaces the FIRST underscore only — correct for the two-word roles we
 * ship today and quietly wrong for the first three-word one. Sentence case
 * rather than Title Case, because a role is a noun in a sentence here, not a
 * heading.
 */
export function roleLabel(role: string): string {
  const words = role.toLowerCase().split('_').filter(Boolean);
  const [first, ...rest] = words;
  if (!first) return role;
  return [first.charAt(0).toUpperCase() + first.slice(1), ...rest].join(' ');
}

/** UUIDs are unreadable in a table but still need to be recognisable across rows. */
export function shortId(id: string | null | undefined): string {
  if (!id) return '—';
  return id.slice(0, 8);
}

/**
 * What one pack of this medicine is called.
 *
 * Stock is counted in packs everywhere in the system, and "50" on its own means
 * nothing at a counter — 50 strips, 50 bottles and 50 tablets are three very
 * different quantities of medicine.
 */
export const PACK_FORMS = [
  'STRIP',
  'BOTTLE',
  'TUBE',
  'VIAL',
  'AMPOULE',
  'SACHET',
  'TIN',
  'JAR',
  'PACK',
] as const;
export type PackForm = (typeof PACK_FORMS)[number];

export function packNoun(form: PackForm | null | undefined, count = 1): string {
  const noun = (form ?? 'PACK').toLowerCase();
  return count === 1 ? noun : `${noun}s`;
}

export function formatPacks(count: number, form: PackForm | null | undefined): string {
  return `${formatNumber(count)} ${packNoun(form, count)}`;
}

/**
 * The dispensable unit inside a pack — a tablet, a millilitre, a gram.
 *
 * Named, not guessed. Every screen that used to write "units" can name the
 * thing, and the two questions a counter actually asks — "how many strips" and
 * "how many tablets" — stop looking like the same question.
 */
export const BASE_UNITS = [
  'TABLET',
  'CAPSULE',
  'ML',
  'GM',
  'IU',
  'DROP',
  'SACHET',
  'PIECE',
] as const;
export type BaseUnit = (typeof BASE_UNITS)[number];

const UNIT_WORDS: Record<BaseUnit, [one: string, many: string]> = {
  TABLET: ['tablet', 'tablets'],
  CAPSULE: ['capsule', 'capsules'],
  ML: ['ml', 'ml'],
  GM: ['g', 'g'],
  IU: ['IU', 'IU'],
  DROP: ['drop', 'drops'],
  SACHET: ['sachet', 'sachets'],
  PIECE: ['piece', 'pieces'],
};

export function unitNoun(unit: BaseUnit | null | undefined, count = 2): string {
  if (!unit) return count === 1 ? 'unit' : 'units';
  const [one, many] = UNIT_WORDS[unit];
  return count === 1 ? one : many;
}

/** "15 tablets in a strip" — the sentence a receiving screen should be able to write. */
export function describePack(
  unitsPerPack: number | null | undefined,
  unit: BaseUnit | null | undefined,
  form: PackForm | null | undefined,
): string | null {
  if (!unitsPerPack) return null;
  return `${unitsPerPack} ${unitNoun(unit, unitsPerPack)} in a ${packNoun(form)}`;
}

const ONES = [
  '', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten',
  'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen',
  'eighteen', 'nineteen',
];
const TENS = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

function under1000(n: number): string {
  if (n === 0) return '';
  if (n < 20) return ONES[n]!;
  if (n < 100) return `${TENS[Math.floor(n / 10)]!}${n % 10 ? `-${ONES[n % 10]!}` : ''}`;
  return `${ONES[Math.floor(n / 100)]!} hundred${n % 100 ? ` and ${under1000(n % 100)}` : ''}`;
}

/**
 * "7220.00" → "Rupees seven thousand two hundred and twenty only".
 *
 * **Indian grouping, not international**: crore, lakh, thousand — a wholesaler's
 * invoice says "one lakh twenty thousand", never "one hundred and twenty
 * thousand", and this line is the one a court reads when the figures are
 * disputed.
 *
 * Takes the decimal STRING money crosses the wire as, so nothing rounds on the
 * way in. Paise are spelled separately because they are a different unit, not a
 * fraction of the rupee word.
 */
export function amountInWords(amount: string | number): string {
  const paise = Math.round(Number(amount) * 100);
  if (!Number.isFinite(paise)) return '';

  const rupees = Math.floor(Math.abs(paise) / 100);
  const fraction = Math.abs(paise) % 100;

  const parts: string[] = [];
  const crore = Math.floor(rupees / 10_000_000);
  const lakh = Math.floor((rupees % 10_000_000) / 100_000);
  const thousand = Math.floor((rupees % 100_000) / 1000);
  const rest = rupees % 1000;

  if (crore) parts.push(`${under1000(crore)} crore`);
  if (lakh) parts.push(`${under1000(lakh)} lakh`);
  if (thousand) parts.push(`${under1000(thousand)} thousand`);
  if (rest) parts.push(under1000(rest));

  const whole = parts.join(' ').trim() || 'zero';
  const sign = paise < 0 ? 'minus ' : '';
  const sentence = fraction
    ? `${sign}rupees ${whole} and ${under1000(fraction)} paise only`
    : `${sign}rupees ${whole} only`;

  return sentence.charAt(0).toUpperCase() + sentence.slice(1);
}
